import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, inject } from "@angular/core";
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import _ from "lodash";
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from "ng-zorro-antd/date-picker";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzInputDirective, NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from "ng-zorro-antd/message";
import { NzPageHeaderModule } from "ng-zorro-antd/page-header";
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzTableModule } from "ng-zorro-antd/table";
import { UserLoggedService } from "src/app/core/services/userLogged.service";
import { ArticleDto } from "src/app/shared/dto/article.dto";
import { MeasurementUnitDto } from "src/app/shared/dto/measurement-unit.dto";
import { UserDto } from "src/app/shared/dto/user.dto";
import { ArticlesService } from "../../services/articles.service";
import { MeasurementunitsService } from "../../services/measurementunits.service";
import { Subject, takeUntil } from "rxjs";
import { PackagingDto } from "src/app/shared/dto/packaging.dto";
import { PackagingsService } from "../../services/packagings.service";
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormModule } from "ng-zorro-antd/form";
import { NzColDirective, NzRowDirective } from "ng-zorro-antd/grid";
import { DrawerMode } from "src/app/core/enums/drawer-mode";
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from "src/app/core/constants/components";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { NzIconModule } from "ng-zorro-antd/icon";
import { DefaultComponent } from "src/app/shared/services/view.service";
import { LoaderComponent } from "src/app/shared/components/loader/loader.component";

@Component({
    selector: 'app-packagings',
    templateUrl: './packagings.component.html',
    styleUrl: './packagings.component.scss',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzInputModule,
        NzFormModule,
        NzCheckboxModule,
        NzDropDownModule,
        NzToolTipModule,
        NzIconModule,
        LoaderComponent
    ],
})
export class PackagingsComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    packagings: PackagingDto[] = [];
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    fieldsReadOnly = true;
    packagingSelected: PackagingDto = new PackagingDto();
    createdAt: Date = new Date();
    updatedAt: Date = new Date();

    currentUser: UserDto = new UserDto();

    articles: ArticleDto[] = [];
    selectedArticle: ArticleDto = new ArticleDto();
    measurements: MeasurementUnitDto[] = [];
    selectedMeasurement: MeasurementUnitDto = new MeasurementUnitDto();

    isLoading: boolean = true;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        batchNumber: FormControl<string>;
        quantity: FormControl<number>;
        externalBatchNumber: FormControl<string>;
    }>;

    private readonly packagingsService = inject(PackagingsService);
    private readonly articlesService = inject(ArticlesService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly measurementUnitService = inject(MeasurementunitsService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('packagings', '');
        this.validateForm = this.fb.group({
            batchNumber: ['', [Validators.required]],
            quantity: [0, [Validators.required, Validators.min(1)]],
            externalBatchNumber: [''],
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.packagingsService.setupWebSocket();
        this.packagingsService.packagings$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (packagings) => {
                    this.packagings = packagings;
                },
                error: () => {
                    this.message.error('Error cargando formatos');
                }
            });

        await this.articlesService.setupWebSocket();
        this.articlesService.articles$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (articles) => {
                    this.articles = articles;
                },
                error: () => {
                    this.message.error('Error cargando artículos');
                }
            });

        await this.measurementUnitService.setupWebSocket();
        this.measurementUnitService.measurementUnits$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (measures) => {
                    this.measurements = measures;
                    if (this.measurements.length > 0) {
                        this.selectedMeasurement = this.measurements[0];
                    } else {
                        this.selectedMeasurement = new MeasurementUnitDto();
                    }
                },
                error: () => {
                    this.message.error('Error cargando unidades de medida');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.packagingsService.disconnectWebSocket();
        this.measurementUnitService.disconnectWebSocket();
        this.articlesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.packagingSelected = new PackagingDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo formato';
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    view(data: PackagingDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.packagingSelected = data;

        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo formato ' + this.packagingSelected?.batchNumber;
        this.fieldsReadOnly = true;
        this.setData();
        this.openDrawer();
    }

    edit(data: PackagingDto) {
        this.packagingSelected = data;
        this.validateForm.patchValue(data);
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando formato ' + this.packagingSelected?.batchNumber;
        this.fieldsReadOnly = false;
        this.setData();
        this.openDrawer();
    }

    delete(id: number) {
        if (window.confirm('¿Estás seguro de que quieres eliminar este formato? Esta acción no se puede deshacer.')) {
            this.packagingsService.delete(id).subscribe({
                next: (packaging: PackagingDto) => {
                    try {
                        if (packaging == undefined) {
                            throw new Error('Packaging not found');
                        }

                        const index = this.packagings.findIndex((a) => a.id === id);

                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar el formato ${packaging.batchNumber} con id ${id}, formato no encontrado`);
                            throw new Error('Packaging not found');
                        }

                        this.packagings.splice(index, 1);
                        const data = [...this.packagings];
                        this.packagings = data;

                        this.message.create('success', 'Formato eliminado correctamente');
                    } catch (error) {
                        console.error(error);
                        this.message.create('error', 'Error al eliminar el formato');
                    }
                },
                error: (error) => {
                    console.error(error);
                    this.message.create('error', 'Error al eliminar el formato, error de servidor');
                }
            });
        } else {
            console.log('Cancelando eliminación de formato ' + id);
        }
    }

    setData() {
        this.selectedMeasurement = this.measurements.find((m) => m.id === this.packagingSelected.packagingType?.measurementUnitId) as MeasurementUnitDto;
        this.selectedArticle = this.articles.find((a) => a.id === this.packagingSelected.articleId) as ArticleDto;
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.packagingSelected = new PackagingDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    submitForm() {
        // const customPackagingDto: PackagingDto = {
        //     id: this.packagingSelected.id,
        //     batchNumber: (<HTMLInputElement>document.getElementById('batchNumber'))?.value ?? '',
        // 	articleId: this.selectedArticle.id,
        // 	article: this.selectedArticle
        // };

        // if (!this.validForm(customPackagingDto)) {
        //     return;
        // }

        // if (this.isNewPackaging) {
        //     this.packagingsService.save(customPackagingDto).subscribe({
        //         next: (packaging: PackagingDto) => {
        //             try {
        //                 if (packaging == undefined) {
        //                     throw new Error('Error creating packaging');
        //                 }

        //                 this.packagings.push(packaging);

        //                 const data = [...this.packagings];
        //                 this.packagings = data;

        //                 this.message.create('success', 'Formato creado correctamente');
        //                 this.closeDrawer();
        //             } catch (error) {
        //                 console.error('Error creating packaging', error);
        //                 this.message.create('error', 'Error al crear el formato');
        //             }
        //         },
        //         error: (error) => {
        //             console.error('Error creating packaging', error);
        //             this.message.create('error', 'Error al crear el formato, error de servidor');
        //         }
        //     });
        // } else {
        //     this.packagingsService.update(customPackagingDto.id, customPackagingDto).subscribe({
        //         next: (packaging: PackagingDto) => {
        //             try {
        //                 if (packaging == undefined) {
        //                     throw new Error('Error updating packaging');
        //                 }

        //                 const index = this.packagings.findIndex((a) => a.id === packaging.id);

        //                 if (index < 0) {
        //                     this.message.create('error', `No se ha podido actualizar el formato ${customPackagingDto.batchNumber} con id ${customPackagingDto.id}, formato no encontrado`);
        //                     throw new Error('Packaging not found');
        //                 }

        //                 this.packagings[index] = packaging;
        //                 const data = [...this.packagings];
        //                 this.packagings = data;

        //                 this.message.create('success', 'Formato actualizado correctamente');

        //                 this.closeDrawer();
        //             } catch (error) {
        //                 console.error('Error updating packaging', error);
        //                 this.message.create('error', 'Error al actualizar el formato');
        //             }
        //         },
        //         error: (error) => {
        //             console.error('Error updating packaging', error);
        //             this.message.create('error', 'Error al actualizar el formato, error de servidor');
        //         }
        //     });
        // }
    }

    cancelEditing() {
        this.closeDrawer();
    }

    validForm(packaging: PackagingDto): boolean {
        if (!packaging.batchNumber) {
            this.message.create('error', 'El nombre del formato es obligatorio');
            return false;
        }

        if (!packaging.articleId || packaging.articleId <= 0) {
            this.message.create('error', 'Se ha de seleccionar un artículo para el formato');
            return false;
        }

        return true;
    }

    changeMeasure(id: number) {
        this.selectedMeasurement = this.measurements.find((m) => m.id === id) as MeasurementUnitDto;
    }

    changeArticle(id: number) {
        this.selectedArticle = this.articles.find((a) => a.id === id) as ArticleDto;
    }
}
